// Types fuer das Vertraege-Modul (Etappe 1 Foundation)

export type ContractDirection = 'outbound' | 'inbound'

export type ContractStatus = 'draft' | 'active' | 'paused' | 'terminated' | 'expired'

export type BillingCycle = 'monthly' | 'quarterly' | 'yearly' | 'once'

export type ProductCategory = 'license' | 'device' | 'service' | 'support' | 'storage' | 'other'

export type ProductSource = 'also' | 'eset' | 'tanss' | 'cwa' | 'm365' | 'sap' | 'manual'

export type ContractLinkKind = 'document' | 'external_url' | 'sap_ref' | 'tanss_ref' | 'other'

export type ContractCategory = 'customer' | 'supplier' | 'personnel' | 'framework' | 'project'

export const CONTRACT_CATEGORY_LABELS: Record<ContractCategory, string> = {
  customer: 'Kundenprojekt',
  supplier: 'Lieferant',
  personnel: 'Personal / Freelancer',
  framework: 'Rahmenvertrag',
  project: 'Projektvertrag',
}

export interface ContractsOverview {
  totalMonthlyRevenue: number
  totalMargin: number
  marginPercent: number
  activeContracts: number
  totalContracts: number
  deviations: number
  pendingBilling: number
}

export interface ContractListItem {
  id: string
  contractNumber: string
  direction: ContractDirection
  contractCategory: ContractCategory
  customerId: string | null
  customerName: string | null
  partyName: string | null
  partySapCardCode: string | null
  productId: string | null
  productName: string | null
  tariffId: string | null
  name: string | null
  status: ContractStatus
  billingCycle: BillingCycle
  startDate: string
  endDate: string | null
  currency: string
  createdAt: string
  updatedAt: string
}

export type ContractItemUserType = 'normal' | 'light' | 'reduced'

export interface ContractItemUser {
  id: string
  contractItemId: string
  userExternalId: string | null
  customerEmployeeId: string | null
  userType: ContractItemUserType
  adExternalId: string | null
  m365ExternalId: string | null
  tanssExternalId: string | null
  alsoExternalId: string | null
  assignedFrom: string | null
  assignedTo: string | null
  createdAt: string
}

export interface ContractItemAsset {
  id: string
  contractItemId: string
  assetExternalId: string | null
  assetType: string | null
  serialNumber: string | null
  model: string | null
  location: string | null
  refreshDate: string | null
  assignedFrom: string | null
  assignedTo: string | null
  createdAt: string
}

export type QuantityMode = 'fixed' | 'per_user_group' | 'base_plus_per_user_group'

export interface ContractItem {
  id: string
  tenantId: string
  contractId: string
  productId: string
  productName?: string | null
  productUnit?: string | null
  tariffComponentId: string | null
  description: string | null
  sollQuantityX1000: number
  unit: string | null
  unitPriceOverrideCents: number | null
  position: number
  /** Etappe 13: Rabatt in Basispunkten (1000 = 10 %). */
  discountPercentBp: number
  /** Etappe 13: Leistungsmemo / Rechnungs-Positionstext. */
  serviceMemo: string | null
  /** Etappe 13b: ab wann darf die Position abgerechnet werden. */
  itemStartDate: string | null
  /** Etappe 13b: Soft-Delete-Stichtag — ab wann nicht mehr abrechenbar. */
  itemTerminationEffectiveDate: string | null
  /** Etappe 13b: bis zu welchem Zeitraum bereits abgerechnet. */
  paidUntil: string | null
  /** Etappe 13b: Soft-Delete-Flag. */
  isActive: boolean
  /** Etappe 14: Hierarchie. */
  parentItemId: string | null
  isBillable: boolean
  quantityMode: QuantityMode
  userGroupId: string | null
  baseQuantityX1000: number
  perUserQuantityX1000: number
  alsoSubscriptionId: string | null
  m365LicenseSku: string | null
  esetLicenseId: string | null
  tanssArtikelId: string | null
  itemSerialNumber: string | null
  createdAt: string
  updatedAt: string
  users?: ContractItemUser[]
  assets?: ContractItemAsset[]
}

export interface CreateContractItemInput {
  productId: string
  tariffComponentId?: string | null
  description?: string | null
  sollQuantityX1000?: number
  unit?: string | null
  unitPriceOverrideCents?: number | null
  position?: number
  discountPercentBp?: number
  serviceMemo?: string | null
  itemStartDate?: string | null
  itemTerminationEffectiveDate?: string | null
  parentItemId?: string | null
  isBillable?: boolean
  quantityMode?: QuantityMode
  userGroupId?: string | null
  baseQuantityX1000?: number
  perUserQuantityX1000?: number
  alsoSubscriptionId?: string | null
  m365LicenseSku?: string | null
  esetLicenseId?: string | null
  tanssArtikelId?: string | null
  itemSerialNumber?: string | null
}

export type UpdateContractItemInput = Partial<Omit<CreateContractItemInput, 'productId'>>

export interface AddContractItemUserInput {
  userExternalId?: string
  customerEmployeeId?: string
  userType?: ContractItemUserType
  adExternalId?: string | null
  m365ExternalId?: string | null
  tanssExternalId?: string | null
  alsoExternalId?: string | null
  assignedFrom?: string | null
  assignedTo?: string | null
}

export interface AddContractItemAssetInput {
  assetExternalId: string
  assetType?: string | null
  serialNumber?: string | null
  model?: string | null
  location?: string | null
  refreshDate?: string | null
  assignedFrom?: string | null
  assignedTo?: string | null
}

export interface ContractLink {
  id: string
  tenantId: string
  contractId: string
  kind: ContractLinkKind
  label: string | null
  url: string | null
  documentId: string | null
  createdAt: string
}

export interface ContractItemLink {
  id: string
  tenantId: string
  inboundItemId: string
  outboundItemId: string
  allocationPercent: number
  createdAt: string
  inboundItem: {
    id: string
    description: string | null
    sollQuantityX1000: number
    unitPriceOverrideCents: number | null
  } | null
  inboundContract: {
    id: string
    contractNumber: string
    name: string | null
    partyName: string | null
  } | null
}

export interface CreateContractItemLinkInput {
  inboundItemId: string
  outboundItemId: string
  allocationPercent: number
}

export interface ContractDetail {
  id: string
  tenantId: string
  contractNumber: string
  direction: ContractDirection
  contractCategory: ContractCategory
  customerId: string | null
  partySapCardCode: string | null
  partyTanssId: string | null
  partyName: string | null
  productId: string | null
  tariffId: string | null
  name: string | null
  status: ContractStatus
  billingCycle: BillingCycle
  startDate: string
  endDate: string | null
  signingDate: string | null
  billingStartDate: string | null
  noticePeriodDays: number
  noticePeriodUnit: 'days' | 'months'
  autoRenew: boolean
  currency: string
  notes: string | null
  createdById: string | null
  esetPublicLicenseKey: string | null
  esetSyncedAt: string | null
  parentSupplierContractId: string | null
  /** Etappe 10: Sonderkuendigungsrecht-Stichtag (gesetzt bei Hersteller-Anpassung > 20 %). */
  specialTerminationUntil: string | null
  /** Etappe 11: zugeordnetes SLA-Profil. */
  slaProfileId: string | null
  /** Etappe 12: Zeitbudget pro Benutzer in Minuten. */
  timeBudgetMinutesPerUser: number | null
  /** Etappe 12: Stundensatz fuer Mehraufwand in Cent. */
  timeBudgetOvertageHourlyRateCents: number | null
  /** Etappe 13: SAP-Uebernahme-Felder. */
  legacyContractNumber: string | null
  description2: string | null
  minimumTermMonths: number | null
  renewalTermMonths: number | null
  terminationReceivedDate: string | null
  terminationRegisteredDate: string | null
  terminationEffectiveDate: string | null
  terminationDocumentId: string | null
  billingPeriodMode: 'calendar' | 'contract'
  billingTiming: 'advance' | 'arrears'
  contractGroupId: string | null
  sapPaymentTermsCode: string | null
  sapPaymentMethodCode: string | null
  createdAt: string
  updatedAt: string
  // Joined / embedded
  customerName: string | null
  productName: string | null
  tariffName: string | null
  parentSupplierContract: {
    id: string
    contractNumber: string
    name: string | null
    partyName: string | null
  } | null
  items: ContractItem[]
  links: ContractLink[]
  /** Werte der mandanten-spezifischen Zusatzfelder (fieldId → Text). */
  customFieldValues: Record<string, string | null>
}

// ─── Vertrags-Zusatzfelder (UDFs nach SAP-Stil) ─────────────────────────────

export type CustomFieldType = 'text' | 'textarea' | 'number' | 'date' | 'select' | 'boolean'

export type CustomFieldSection =
  | 'identification'
  | 'party'
  | 'service'
  | 'term'
  | 'billing'
  | 'termination'
  | 'additional'

export interface CustomFieldOption {
  value: string
  label: string
}

export interface ContractCustomField {
  id: string
  tenantId: string
  fieldKey: string
  label: string
  fieldType: CustomFieldType
  optionsJson: CustomFieldOption[] | null
  hint: string | null
  required: boolean
  section: CustomFieldSection
  sortOrder: number
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateCustomFieldInput {
  fieldKey?: string
  label: string
  fieldType: CustomFieldType
  optionsJson?: CustomFieldOption[] | null
  hint?: string | null
  required?: boolean
  section?: CustomFieldSection
  sortOrder?: number
  active?: boolean
}

export type UpdateCustomFieldInput = Partial<CreateCustomFieldInput>

export interface DerivedContract extends ContractListItem {
  parentSupplierContractId: string | null
}

export interface DerivedContractsResponse {
  items: DerivedContract[]
  summary: {
    count: number
    activeCount: number
  }
}

export interface CreateContractInput {
  contractNumber?: string
  direction: ContractDirection
  contractCategory?: ContractCategory
  customerId?: string | null
  partySapCardCode?: string | null
  partyTanssId?: string | null
  partyName?: string | null
  productId?: string | null
  tariffId?: string | null
  name?: string | null
  status?: ContractStatus
  billingCycle?: BillingCycle
  startDate: string
  endDate?: string | null
  signingDate?: string | null
  billingStartDate?: string | null
  noticePeriodDays?: number
  noticePeriodUnit?: 'days' | 'months'
  autoRenew?: boolean
  currency?: string
  notes?: string | null
  parentSupplierContractId?: string | null
  slaProfileId?: string | null
  legacyContractNumber?: string | null
  description2?: string | null
  minimumTermMonths?: number | null
  renewalTermMonths?: number | null
  terminationReceivedDate?: string | null
  terminationRegisteredDate?: string | null
  terminationEffectiveDate?: string | null
  terminationDocumentId?: string | null
  billingPeriodMode?: 'calendar' | 'contract'
  billingTiming?: 'advance' | 'arrears'
  contractGroupId?: string | null

  timeBudgetMinutesPerUser?: number | null
  timeBudgetOvertageHourlyRateCents?: number | null
  sapPaymentTermsCode?: string | null
  sapPaymentMethodCode?: string | null
}

export type UpdateContractInput = Partial<CreateContractInput> & {
  /** Map fieldId → value-text. Leerstring/null loescht den Wert. */
  customFieldValues?: Record<string, string | null>
}

export interface Product {
  id: string
  tenantId: string
  sku: string | null
  name: string
  description: string | null
  category: ProductCategory
  unit: string
  defaultSource: ProductSource
  defaultSupplierSapCardCode: string | null
  sapItemCode: string | null
  alsoSku: string | null
  esetProductId: string | null
  m365LicenseSku: string | null
  contractGroupId: string | null
  setupFeeCents: number | null
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface ProductComponent {
  id: string
  tenantId: string
  productId: string
  componentProductId: string
  quantityPerUnitX1000: number
  position: number
  createdAt: string
}

export interface ProductDetail extends Product {
  components: ProductComponent[]
}

export interface CreateProductInput {
  name: string
  unit: string
  sku?: string | null
  description?: string | null
  category?: ProductCategory
  defaultSource?: ProductSource
  defaultSupplierSapCardCode?: string | null
  sapItemCode?: string | null
  alsoSku?: string | null
  esetProductId?: string | null
  m365LicenseSku?: string | null
  contractGroupId?: string | null
  setupFeeCents?: number | null
  active?: boolean
}

export type SetupFeeStatus = 'pending' | 'billed'

export interface SetupFeeItem {
  id: string
  tenantId: string
  contractId: string
  contractItemId: string
  contractItemUserId: string
  productId: string | null
  description: string | null
  amountCents: number
  currency: string
  status: SetupFeeStatus
  billingRunId: string | null
  createdAt: string
}

export type UpdateProductInput = Partial<CreateProductInput>

export interface ArticleLite {
  id: string
  itemCode: string
  itemName: string
  manufacturer: string | null
  unit: string
  /** Verkaufspreis in EUR (vom Backend bereits umgerechnet aus Cents). */
  salesPrice: number
  /** Einkaufspreis in EUR (vom Backend bereits umgerechnet aus Cents). */
  purchasePrice: number
  syncStatus: 'synced' | 'local_only' | 'conflict'
}

export interface ArticlesPage {
  data: ArticleLite[]
  total: number
  page: number
  pageSize: number
}

// ─── Tarife & Staffeln ──────────────────────────────────────────────────────

export interface Tariff {
  id: string
  tenantId: string
  productId: string
  productName?: string | null
  name: string
  billingCycle: BillingCycle
  validFrom: string
  validTo: string | null
  currency: string
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface PricingTier {
  id: string
  tariffComponentId: string | null
  tariffId: string | null
  minQtyX1000: number
  maxQtyX1000: number | null
  unitPriceCents: number
  position: number
}

export interface TariffComponent {
  id: string
  tariffId: string
  componentProductId: string
  unitCostCents: number | null
  unitPriceCents: number
  position: number
  product: {
    id: string
    name: string
    unit: string
    sku: string | null
    sapItemCode: string | null
  } | null
  /** Wenn das Komponentenprodukt einen SAP-Artikel hinterlegt hat: aktuelle SAP-Preise als Vorschlag. */
  sapArticle: {
    itemCode: string
    itemName: string
    salesPriceCents: number
    purchasePriceCents: number
  } | null
  tiers: PricingTier[]
}

export interface TariffDetail extends Tariff {
  components: TariffComponent[]
  tariffLevelTiers: PricingTier[]
}

export interface CreateTariffInput {
  productId: string
  name: string
  billingCycle?: BillingCycle
  validFrom?: string
  validTo?: string | null
  currency?: string
  active?: boolean
}

export type UpdateTariffInput = Partial<Omit<CreateTariffInput, 'productId'>>

export interface CreateTariffComponentInput {
  componentProductId: string
  unitPriceCents: number
  unitCostCents?: number | null
  position?: number
}

export type UpdateTariffComponentInput = Partial<Omit<CreateTariffComponentInput, 'componentProductId'>>

export interface CreatePricingTierInput {
  minQtyX1000: number
  maxQtyX1000?: number | null
  unitPriceCents: number
  position?: number
}

export type UpdatePricingTierInput = Partial<CreatePricingTierInput>

// ─── Reconciliation (Etappe 4) ──────────────────────────────────────────────

export type ReconciliationSource = 'also' | 'eset' | 'tanss' | 'cwa' | 'm365' | 'sap' | 'manual'

export type FindingType =
  | 'qty_mismatch'
  | 'missing_in_external'
  | 'missing_in_contract'
  | 'license_expired'
  | 'license_expiring'
  | 'asset_offline'
  | 'other'

export type FindingSeverity = 'info' | 'warning' | 'critical'

export type FindingStatus = 'open' | 'acknowledged' | 'resolved' | 'ignored' | 'auto_resolved'

export type RunStatus = 'running' | 'success' | 'partial' | 'error'

export interface ReconciliationFinding {
  id: string
  tenantId: string
  runId: string | null
  source: ReconciliationSource
  contractId: string | null
  contractItemId: string | null
  contractNumber?: string | null
  contractName?: string | null
  findingType: FindingType
  severity: FindingSeverity
  sollQuantityX1000: number | null
  istQuantityX1000: number | null
  deltaX1000: number | null
  externalRef: string | null
  externalLabel: string | null
  description: string | null
  status: FindingStatus
  detectedAt: string
  resolvedAt: string | null
  resolvedById: string | null
  notes: string | null
}

export interface ReconciliationRun {
  id: string
  tenantId: string
  source: ReconciliationSource
  status: RunStatus
  findingsCount: number
  contractsChecked: number
  errorMessage: string | null
  startedAt: string
  finishedAt: string | null
  triggeredById: string | null
}

export interface ReconciliationSummary {
  openFindings: number
  criticalFindings: number
  lastRun: ReconciliationRun | null
  availableSources: ReconciliationSource[]
}

export interface RunReconciliationResult {
  runId: string
  findingsCount: number
  status: 'success' | 'partial' | 'error'
  errorMessage?: string
}

// ─── Billing (Etappe 5) ─────────────────────────────────────────────────────

export type BillingRunStatus = 'draft' | 'approved' | 'submitted' | 'closed' | 'error'
export type BillingItemSapStatus = 'pending' | 'submitted' | 'confirmed' | 'error'

export type BillingRunMode = 'automatic' | 'manual_approval'
export type SapEnvironment = 'test' | 'production'

export interface BillingRun {
  id: string
  tenantId: string
  periodStart: string
  periodEnd: string
  status: BillingRunStatus
  mode: BillingRunMode
  sapEnvironment: SapEnvironment | null
  contractsBilled: number
  itemsBilled: number
  totalAmountCents: number
  currency: string
  notes: string | null
  errorMessage: string | null
  triggeredById: string | null
  approvedById: string | null
  approvedAt: string | null
  startedAt: string
  finishedAt: string | null
  completedAt: string | null
}

export interface FractionalPeriodSegment {
  start: string
  end: string
  fractionX1000: number
}

export interface BillingItem {
  id: string
  tenantId: string
  runId: string
  contractId: string
  contractItemId: string | null
  productId: string | null
  description: string | null
  quantityX1000: number
  unitPriceCents: number
  totalCents: number
  currency: string
  sapStatus: BillingItemSapStatus
  sapDocEntry: number | null
  sapDocNumber: number | null
  sapErrorMessage: string | null
  pricingNote: string | null
  periodStart: string | null
  periodEnd: string | null
  fullPeriods: number
  fractionalPeriodsJson: FractionalPeriodSegment[] | null
  periodFractionX1000: number
  totalPeriodsX1000: number
  discountPercentBp: number
  appliedToPaidUntil: boolean
  isStructureLine: boolean
  contractNumber?: string | null
  contractName?: string | null
  createdAt: string
}

export interface BillingRunDetail extends BillingRun {
  items: BillingItem[]
}

export interface BillingPreviewItem {
  contractId: string
  contractNumber: string
  contractName: string | null
  contractItemId: string | null
  productId: string | null
  description: string | null
  quantityX1000: number
  unitPriceCents: number
  totalCents: number
  currency: string
  pricingNote: string | null
  periodStart?: string
  periodEnd?: string
  fullPeriods?: number
  fractionalPeriods?: FractionalPeriodSegment[]
  periodFractionX1000?: number
  totalPeriodsX1000?: number
  discountPercentBp?: number
  isStructureLine?: boolean
}

export interface BillingPreview {
  periodStart: string
  periodEnd: string
  contractsBilled: number
  itemsBilled: number
  totalAmountCents: number
  currency: string
  items: BillingPreviewItem[]
}

export interface RunBillingInput {
  year?: number
  month?: number
  periodStart?: string
  periodEnd?: string
  notes?: string | null
  /** Etappe 13d: AUTOMATIC = direkt approved + an SAP uebergeben; MANUAL_APPROVAL = nach Freigabe. */
  mode?: BillingRunMode
  sapEnvironment?: SapEnvironment
}

// ─── Vertragsgenerator (Etappe 7a) ──────────────────────────────────────────

export type ContractDocumentStatus =
  | 'draft'
  | 'finalized'
  | 'sent_for_signature'
  | 'signed'
  | 'declined'
  | 'expired'
  | 'error'

export type SignatureProvider = 'skribble' | 'yousign' | 'none'

export interface ContractTemplate {
  id: string
  tenantId: string
  name: string
  description: string | null
  productId: string | null
  productName?: string | null
  mimeType: string | null
  originalFileName: string | null
  fileSize: number | null
  placeholders: string[] | null
  version: number
  active: boolean
  parentVersionId: string | null
  isMaterialChange: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateContractTemplateInput {
  name: string
  description?: string | null
  productId?: string | null
  fileContentBase64: string
  originalFileName?: string
}

export interface UpdateContractTemplateInput {
  name?: string
  description?: string | null
  productId?: string | null
  active?: boolean
  isMaterialChange?: boolean
}

export interface ReplaceContractTemplateInput {
  fileContentBase64: string
  originalFileName?: string
  isMaterialChange?: boolean
}

export interface TemplateDiff {
  a: { id: string; version: number; name: string }
  b: { id: string; version: number; name: string }
  diff: { added: string[]; removed: string[]; unchanged: string[] }
}

export interface AffectedContract {
  contractId: string
  templateId: string | null
  contractNumber: string
  partyName: string | null
  status: string
  customerId: string | null
}

export interface ContractTemplateSend {
  id: string
  contractId: string
  templateId: string
  templateVersion: number | null
  sentAt: string | null
  recipientEmail: string
  signatureStatus: string | null
  signatureExternalId: string | null
  createdAt: string
  contractNumber: string | null
  partyName: string | null
}

export interface BulkSendRecipient {
  contractId: string
  email: string
}

export interface BulkSendInput {
  recipients: BulkSendRecipient[]
  subject?: string
  messageHtml?: string
}

export interface BulkSendResult {
  results: Array<{ contractId: string; email: string; ok: boolean; error?: string }>
}

export interface ContractDocument {
  id: string
  tenantId: string
  contractId: string
  templateId: string | null
  templateName?: string | null
  version: number
  fileName: string
  mimeType: string
  fileSize: number
  status: ContractDocumentStatus
  signatureProvider: SignatureProvider
  signatureExternalId: string | null
  signatureStatus: string | null
  signedAt: string | null
  notes: string | null
  generatedAt: string
}

export interface SapBusinessPartner {
  CardCode: string
  CardName: string
  CardType?: string
  EmailAddress?: string | null
  Phone1?: string | null
}

export interface CustomerLite {
  id: string
  name: string
  tanssId: string | null
  sapCardCode: string | null
}

// ─── Vertragsgruppen (Etappe 13) ────────────────────────────────────────────

export interface ContractGroup {
  id: string
  tenantId: string
  name: string
  description: string | null
  customerId: string | null
  sapCardCode: string | null
  billingLookaheadDays: number
  active: boolean
  createdAt: string
  updatedAt: string
  contractsCount?: number
  contractTypesCount?: number
}

export interface ContractGroupDetail extends ContractGroup {
  contracts: Array<{ id: string; contractNumber: string; name: string | null }>
  contractTypes: Array<{
    id: string
    sku: string | null
    name: string
    category: ProductCategory
    unit: string
    active: boolean
  }>
}

export interface CreateContractGroupInput {
  name: string
  description?: string | null
  customerId?: string | null
  sapCardCode?: string | null
  billingLookaheadDays?: number
  active?: boolean
}

export type UpdateContractGroupInput = Partial<CreateContractGroupInput>

// ─── Vertrags-Versionen / Milestones (Etappe 13) ────────────────────────────

export interface ContractVersion {
  id: string
  tenantId: string
  contractId: string
  validFrom: string
  comment: string | null
  snapshot: Record<string, unknown> | null
  createdAt: string
  createdById: string | null
}

export interface CreateContractVersionInput {
  validFrom: string
  comment?: string | null
}

// ─── User-Klassen pro Vertrag (Etappe 14) ──────────────────────────────────

export interface ContractUserGroup {
  id: string
  tenantId: string
  contractId: string
  name: string
  description: string | null
  count: number
  minQuantityPercent: number | null
  tanssFilter: Record<string, unknown> | null
  lastSyncedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface PendingUserGroupReduction {
  id: string
  contractUserGroupId: string
  oldCount: number
  newCount: number
  effectiveAt: string
  createdAt: string
}

export interface CreateContractUserGroupInput {
  name: string
  description?: string | null
  count?: number
  tanssFilter?: Record<string, unknown> | null
}

export type UpdateContractUserGroupInput = Partial<CreateContractUserGroupInput>
